import {
  addDayOffset,
  createDateString,
  DateString,
  dateStringFromDate,
  lastSundayDate,
  splitDateString,
} from './date_string.ts';

// When a church's passed Sunday counts as passed, and until when every reader goes on filling it on screen (issue
// #4192). The writes themselves are in passed_sundays.ts; the fill window is read by repeating_rows.ts. Issue #4192:
// "Until a passed Sunday has been written down, every screen goes on showing it filled". A reader that knows how far
// the server has written the church down (`writtenThrough`, GET /api/passed-sundays) fills every passed Sunday after
// it, however long the server has been behind; one that has not heard from the server falls back to a clock, the
// Sunday before the current one until its Monday at passedSundayShownFilledUntilHourUtc UTC.

// The hour of Monday (UTC) from which the Sunday before counts as passed everywhere Sing Your Part is used: Sunday
// ends at 10:00 UTC Monday in Hawaii (UTC-10), the westernmost US time zone. The server writes a passed Sunday down
// from this hour on, checking every 15 minutes (server/src/passed_sunday_writer.ts).
export const passedSundayWriteHourUtc = 10;
// The hour of Monday (UTC) by which the write has normally happened: until then a reader that does not know how far
// the server has written (getFillStartSunday without `writtenThrough`) goes on filling the passed Sunday on screen, so
// nothing goes blank between the Sunday passing and the write (issue #4192).
export const passedSundayShownFilledUntilHourUtc = 12;

// The easternmost time zone in use, UTC+14 (Line Islands): a Sunday has passed for some admin once its Monday has begun
// there, at Sunday 10:00 UTC. An admin's planner counts a Sunday as passed at its own local midnight (issue #4209).
const easternmostUtcOffsetHours = 14;

const millisecondsPerHour = 60 * 60 * 1000;

// The most recent Sunday that has passed everywhere as of `now`: Sunday S is passed from Monday S+1 at
// passedSundayWriteHourUtc UTC. Read in UTC, so the answer does not depend on the machine's time zone.
export function getLastPassedSunday(now: Date): DateString {
  const shifted = new Date(now.getTime() - passedSundayWriteHourUtc * millisecondsPerHour);
  return lastSundayDate(createDateString(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()));
}

// Whether `sunday` has passed somewhere as of `now`: its Monday has begun in the easternmost time zone. An admin's
// planner may have written it down from then on (the server accepts its mark), so a Sunday that has not can be
// written down by no one yet.
export function hasSundayPassedAnywhere(sunday: DateString, now: Date): boolean {
  const { year, month, day } = splitDateString(sunday)!;
  return now.getTime() >= Date.UTC(year, month, day + 1) - easternmostUtcOffsetHours * millisecondsPerHour;
}

// The first Sunday the fill applies to on screen as of `now`, given the current Sunday (thisSundayDate of the reader's
// today). With `writtenThrough`, the last Sunday the server has written the church down through
// (readWrittenThrough in server/src/passed_sunday_writer.ts), the Sunday after it, or the current Sunday when that is
// earlier: every passed Sunday not yet written goes on showing its fill, and `now` does not matter. Without it, the
// fallback of a reader that has not heard from the server: the Sunday before the current one while `now` is before
// that Sunday's Monday at passedSundayShownFilledUntilHourUtc UTC, as it may not be written down yet; otherwise the
// current Sunday.
export function getFillStartSunday(now: Date, currentSunday: DateString, writtenThrough?: DateString): DateString {
  if (writtenThrough !== undefined) {
    const firstUnwritten = getFirstUnwrittenSunday(writtenThrough);
    return firstUnwritten < currentSunday ? firstUnwritten : currentSunday;
  }
  const previousSunday = addDayOffset(currentSunday, -7);
  const { year, month, day } = splitDateString(previousSunday)!;
  const shownFilledUntil = Date.UTC(year, month, day + 1, passedSundayShownFilledUntilHourUtc);
  return now.getTime() < shownFilledUntil ? previousSunday : currentSunday;
}

// The first Sunday the server has not written a church down through, given `writtenThrough`, the last one it has
// (readWrittenThrough in server/src/passed_sunday_writer.ts)
export function getFirstUnwrittenSunday(writtenThrough: DateString): DateString {
  return addDayOffset(writtenThrough, 7);
}

// The instant a reader on `today` reads at, for getFillStartSunday: `realNow` when `today` is its local date, as it
// is for a reader on the wall clock; otherwise local noon of `today`. An injected date (a story, a test) then reads as
// midday, the same on every run, which is past Monday noon UTC in every US time zone, so on a Monday the Sunday before
// counts as written down.
export function getReaderInstant(today: DateString, realNow: Date = new Date()): Date {
  if (dateStringFromDate(realNow) === today) {
    return realNow;
  }
  const { year, month, day } = splitDateString(today)!;
  return new Date(year, month, day, 12);
}
